import { memo, useEffect, useState } from "react";
import { Euro, BatteryCharging, BedDouble, UtensilsCrossed, Zap, RefreshCw, Target, Pencil, Receipt, Coins, AlertTriangle, Ticket } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { CORE_CATS, isToll, fmtDay, dayWerkelijk, outsideTotal } from "./budget/budgetUtils";
import { ExtraCostsCard } from "./budget/ExtraCostsCard";
import { DupeDetector } from "./budget/DupeDetector";
import { CategoryPie } from "./budget/CategoryPie";
import { PerDayList } from "./budget/PerDayList";

const CompareRow = ({ icon: Icon, color, label, actual, planned, note, testId }) => {
  const hasPlan = planned != null;
  const over = hasPlan && actual > planned + 0.005;
  const pct = hasPlan ? (planned > 0 ? Math.min((actual / planned) * 100, 100) : (actual > 0 ? 100 : 0)) : 0;
  const delta = hasPlan ? actual - planned : null;
  return (
    <div data-testid={testId} className="py-2.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-2 font-semibold min-w-0"><Icon className={`w-4 h-4 shrink-0 ${color}`} /> {label}</span>
        <span className="font-mono text-xs shrink-0">
          € {actual.toFixed(2)}{hasPlan && <span className="text-muted-foreground"> / € {planned.toFixed(2)} raming</span>}
        </span>
      </div>
      {hasPlan && (
        <div className="h-2 rounded-full bg-secondary mt-1.5 overflow-hidden">
          <div className={`h-full rounded-full ${over ? "bg-[#E07A5F]" : "bg-[#52B788]"} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-1">
        {hasPlan && Math.abs(delta) > 0.005 && (
          <span className={`font-bold ${over ? "text-[#E07A5F]" : "text-[#2D6A4F]"}`}>
            {over ? `€ ${delta.toFixed(2)} boven raming` : `€ ${Math.abs(delta).toFixed(2)} onder raming`}
          </span>
        )}
        {hasPlan && Math.abs(delta) > 0.005 && note ? " · " : ""}{note}
      </p>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, color }) => (
  <div className="bg-card rounded-2xl p-5 flex items-center gap-4">
    <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </span>
    <div className="flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-mono font-bold text-lg">€ {value?.toFixed(2)}</p>
    </div>
  </div>
);

const BudgetChart = memo(({ perDay, extras, dailyBudget }) => {
  if ((perDay || []).length < 2) return null;
  const spread = outsideTotal(extras, perDay) / perDay.length;
  const data = perDay.map((d) => {
    const wk = dayWerkelijk(d, extras, spread);
    return {
      dag: d.day_number,
      raming: Math.round(d.total * 100) / 100,
      werkelijk: wk != null ? Math.round(wk * 100) / 100 : null,
    };
  });
  const hasActual = data.some((x) => x.werkelijk != null);
  return (
    <div className="bg-card rounded-2xl p-5" data-testid="budget-chart">
      <p className="font-heading font-bold text-sm">Kosten per dag — raming vs. werkelijk</p>
      {!hasActual && <p className="text-[11px] text-muted-foreground mt-0.5">Koppel onkosten aan een dag om de vergelijkingslijn te zien.</p>}
      <div className="mt-3">
        <ResponsiveContainer width="100%" height={220} debounce={200}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: -6, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="dag" tickFormatter={(v) => `d${v}`} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} width={52} />
            <RTooltip formatter={(v, name) => [`€ ${Number(v).toFixed(2)}`, name === "raming" ? "Raming" : "Werkelijk"]} labelFormatter={(v) => { const dd = perDay.find((d) => d.day_number === v)?.date; return `Dag ${v}${dd ? ` · ${fmtDay(dd)}` : ""}`; }} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
            <Legend formatter={(v) => (v === "raming" ? "Raming" : "Werkelijk")} wrapperStyle={{ fontSize: 12 }} />
            {dailyBudget > 0 && <ReferenceLine y={dailyBudget} stroke="#B5838D" strokeDasharray="4 4" label={{ value: `dagbudget €${dailyBudget}`, fontSize: 10, fill: "#B5838D", position: "insideTopRight" }} />}
            <Line type="monotone" dataKey="raming" stroke="#3A6EA5" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 2.5 }} isAnimationActive={false} />
            <Line type="monotone" dataKey="werkelijk" stroke="#2D6A4F" strokeWidth={2.5} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export const BudgetTab = ({ trip, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(trip.daily_budget ?? "");
  const [savingTarget, setSavingTarget] = useState(false);
  const [tollEdit, setTollEdit] = useState(false);
  const [tollVal, setTollVal] = useState(trip.toll_budget_eur ?? "");
  const [tollBusy, setTollBusy] = useState(false);
  const [splitData, setSplitData] = useState({ members: [], expenses: [] });
  const [chartReady, setChartReady] = useState(false);
  const budget = trip.budget;
  const extras = trip.extra_costs || [];

  useEffect(() => {
    const cb = () => setChartReady(true);
    const idle = typeof window.requestIdleCallback === "function";
    const idr = idle ? window.requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 400);
    return () => (idle ? window.cancelIdleCallback?.(idr) : clearTimeout(idr));
  }, []);

  useEffect(() => {
    const loadSplit = () => api.get(`/trips/${trip.id}/split`)
      .then((r) => setSplitData({ members: r.data.members || [], expenses: r.data.expenses || [] }))
      .catch(() => {});
    loadSplit();
    window.addEventListener("vr-split-refresh", loadSplit);
    return () => window.removeEventListener("vr-split-refresh", loadSplit);
  }, [trip.id]);

  const saveToll = async () => {
    setTollBusy(true);
    try {
      await api.patch(`/trips/${trip.id}/budget/tolls`, { toll_budget_eur: tollVal === "" ? null : parseFloat(tollVal) });
      toast.success("Tol/péage-raming opgeslagen — budget herrekend");
      setTollEdit(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTollBusy(false);
    }
  };

  const recalc = async () => {
    setLoading(true);
    try {
      await api.post(`/trips/${trip.id}/budget/recalc`);
      toast.success("Budget herberekend!");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await api.patch(`/trips/${trip.id}/budget/target`, { daily_budget: target === "" ? null : parseFloat(target) });
      toast.success("Dagbudget opgeslagen!");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingTarget(false);
    }
  };

  if (!budget) {
    return (
      <div className="bg-card rounded-2xl p-8 text-center" data-testid="budget-empty">
        <Euro className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground mb-4">Nog geen budget berekend voor deze trip.</p>
        <Button data-testid="budget-recalc-button" onClick={recalc} disabled={loading} className="rounded-full bg-primary hover:bg-[#1B4332]">
          {loading ? "Berekenen..." : "Bereken budget"}
        </Button>
      </div>
    );
  }

  const numDays = budget.per_day?.length || 0;
  const targetTotal = trip.daily_budget ? trip.daily_budget * numDays : null;
  const diff = targetTotal != null ? targetTotal - budget.grand_total : null;
  const daysWithExtras = (budget.per_day || []).filter((d) => extras.some((e) => e.day_number === d.day_number));
  const daylessTotal = extras.filter((e) => e.day_number == null).reduce((a, e) => a + (e.amount_eur || 0), 0);
  const werkelijkTotNu = daysWithExtras.reduce((a, d) => a + dayWerkelijk(d, extras), 0) + daylessTotal;
  const plannedSumForActualDays = daysWithExtras.reduce((a, d) => a + d.total, 0);
  const actualDelta = werkelijkTotNu - plannedSumForActualDays;
  const structOver = daysWithExtras.length >= 2 && actualDelta > Math.max(25, 0.1 * plannedSumForActualDays);
  const cmpCat = (slug, plannedOf) => {
    const catExtras = extras.filter((e) => e.category === slug);
    const actual = catExtras.reduce((a, e) => a + (e.amount_eur || 0), 0);
    if (actual <= 0) return null;
    const dayNums = new Set(catExtras.filter((e) => e.day_number != null).map((e) => e.day_number));
    const planned = dayNums.size ? (budget.per_day || []).filter((d) => dayNums.has(d.day_number)).reduce((a, d) => a + plannedOf(d), 0) : null;
    return { actual, planned, n: dayNums.size, hasDayless: catExtras.some((e) => e.day_number == null) };
  };
  const cmpCharge = cmpCat("laden", (d) => (d.charging || 0) + (d.overnight_charging || 0));
  const cmpStay = cmpCat("verblijf", (d) => d.stay || 0);
  const cmpFood = cmpCat("eten", (d) => d.food || 0);
  const tollActual = extras.filter(isToll).reduce((a, e) => a + (e.amount_eur || 0), 0);
  const otherExtras = extras.filter((e) => !CORE_CATS.includes(e.category) && !isToll(e)).reduce((a, e) => a + (e.amount_eur || 0), 0);
  const tollPlanned = budget.tolls_total || 0;
  const hasCompare = cmpCharge || cmpStay || cmpFood || tollActual > 0 || tollPlanned > 0;
  const cmpNote = (c, unit) => `o.b.v. ${c.n} dag${c.n === 1 ? "" : "en"} met onkosten${c.hasDayless ? " + posten zonder dag" : ""}${unit || ""}`;

  return (
    <div className="space-y-4" data-testid="budget-tab">
      <div className="bg-primary rounded-2xl p-6 text-primary-foreground">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold opacity-80">Totaalraming trip</p>
        <p data-testid="budget-grand-total" className="font-mono font-extrabold text-4xl mt-1">€ {budget.grand_total?.toFixed(2)}</p>
        {extras.length > 0 && (
          <p data-testid="budget-actual-total" className={`text-sm font-semibold mt-2 ${actualDelta <= 0 ? "text-[#B7E4C7]" : "text-[#FFB4A2]"}`}>
            Werkelijk tot nu: € {werkelijkTotNu.toFixed(2)}
            {daylessTotal > 0.005 && <> (waarvan € {daylessTotal.toFixed(2)} vooraf/achteraf of zonder dag)</>}
            {daysWithExtras.length > 0 && <> — {actualDelta <= 0 ? `€ ${Math.abs(actualDelta).toFixed(0)} onder` : `€ ${actualDelta.toFixed(0)} boven`} raming</>}
          </p>
        )}
        {diff != null && (
          <p data-testid="budget-target-status" className={`text-sm font-semibold mt-1 ${diff >= 0 ? "text-[#B7E4C7]" : "text-[#FFB4A2]"}`}>
            {diff >= 0 ? `€ ${diff.toFixed(0)} onder je budget van € ${targetTotal.toFixed(0)}` : `€ ${Math.abs(diff).toFixed(0)} boven je budget van € ${targetTotal.toFixed(0)}`}
          </p>
        )}
      </div>

      {structOver && (
        <div data-testid="budget-overrun-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800">Je zit structureel boven je raming</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Werkelijk € {werkelijkTotNu.toFixed(0)} vs. raming € {plannedSumForActualDays.toFixed(0)} voor de {daysWithExtras.length} dagen met onkosten
              (+€ {actualDelta.toFixed(0)}{plannedSumForActualDays > 0 ? ` / +${Math.round((actualDelta / plannedSumForActualDays) * 100)}%` : ""}).
              Check hieronder welke categorie het meest afwijkt.
            </p>
          </div>
        </div>
      )}

      <div className="bg-card rounded-2xl p-5">
        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-primary" /> Jouw dagbudget (€ per dag)</p>
        <div className="flex gap-2">
          <input data-testid="daily-budget-input" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="bv. 200"
            className="flex-1 bg-secondary rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
          <Button data-testid="daily-budget-save" onClick={saveTarget} disabled={savingTarget}
            className="rounded-full bg-primary hover:bg-[#1B4332] px-5 active:scale-95 transition-[transform,background-color] duration-200">
            {savingTarget ? "..." : "Opslaan"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Row icon={Zap} label={trip.travel_mode === "moto" ? "Brandstof onderweg" : "Snelladen onderweg"} value={budget.charging_total} color="bg-[#2D6A4F]" />
        {trip.travel_mode !== "moto" && <Row icon={BatteryCharging} label="Overnacht laden" value={budget.overnight_charging_total} color="bg-[#52B788]" />}
        <Row icon={BedDouble} label="Verblijf" value={budget.stays_total} color="bg-[#3A6EA5]" />
        <Row icon={UtensilsCrossed} label="Eten & drinken" value={budget.food_total} color="bg-[#E07A5F]" />
        {(budget.vignettes || []).length > 0 && (
          <div className="bg-card rounded-2xl p-5 flex items-center gap-4 sm:col-span-2" data-testid="budget-vignettes-row">
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#8E7DBE]">
              <Ticket className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">Vignetten (automatisch o.b.v. je routelanden)</p>
              <p data-testid="budget-vignettes-total" className="font-mono font-bold text-lg">€ {(budget.vignettes_total || 0).toFixed(2)}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{budget.vignettes.map((v) => `${v.land} €${v.eur.toFixed(2).replace(".00", "").replace(".", ",")}`).join(" · ")} — kooplinks vind je bij "Belangrijke info" op de plan-tab</p>
            </div>
          </div>
        )}
        <div className="bg-card rounded-2xl p-5 flex items-center gap-4 sm:col-span-2" data-testid="budget-toll-row">
          <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#B5838D]">
            <Coins className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">Tol / péage (eigen raming voor de hele trip)</p>
            {tollEdit ? (
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="toll-budget-input" type="number" min="0" step="1" value={tollVal} autoFocus
                  onChange={(e) => setTollVal(e.target.value)} placeholder="bv. 120"
                  className="w-28 bg-secondary rounded-lg px-3 py-1.5 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
                <button data-testid="toll-budget-save" onClick={saveToll} disabled={tollBusy}
                  className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-bold px-4 py-1.5 active:scale-95 transition-[transform,background-color] duration-200">
                  {tollBusy ? "..." : "Opslaan"}
                </button>
                <button data-testid="toll-budget-cancel" onClick={() => setTollEdit(false)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-[color] duration-200">Annuleer</button>
              </div>
            ) : (
              <p data-testid="budget-tolls-total" className="font-mono font-bold text-lg">€ {(budget.tolls_total || 0).toFixed(2)}</p>
            )}
            {!tollEdit && trip.avoid_toll && <p className="text-[10px] text-muted-foreground mt-0.5">Je route vermijdt tolwegen — waarschijnlijk € 0</p>}
            {!tollEdit && !trip.avoid_toll && !budget.tolls_total && <p className="text-[10px] text-muted-foreground mt-0.5">Richtprijs: ±€ 1 per 10 km autoroute in FR/IT{(budget.vignettes || []).length > 0 ? " — vignetten zijn al apart geraamd hierboven" : " · vignet CH € 42"}</p>}
          </div>
          {!tollEdit && (
            <button data-testid="toll-budget-edit" onClick={() => { setTollVal(trip.toll_budget_eur ?? ""); setTollEdit(true); }}
              title="Tol/péage-raming aanpassen"
              className="p-2 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {hasCompare && (
        <div className="bg-card rounded-2xl p-5" data-testid="budget-compare-card">
          <div className="flex items-center justify-between mb-1">
            <p className="font-heading font-bold text-sm">Raming vs. werkelijk</p>
            <p className="text-[11px] text-muted-foreground hidden sm:block">berekend uit de categorieën van je onkosten</p>
          </div>
          <div className="divide-y divide-border/60">
            {cmpCharge && (
              <CompareRow testId="compare-charging" icon={Zap} color="text-[#2D6A4F]" label={trip.travel_mode === "moto" ? "Brandstof" : "Laden"} actual={cmpCharge.actual} planned={cmpCharge.planned}
                note={cmpNote(cmpCharge)} />
            )}
            {cmpStay && (
              <CompareRow testId="compare-stay" icon={BedDouble} color="text-[#3A6EA5]" label="Verblijf" actual={cmpStay.actual} planned={cmpStay.planned}
                note={cmpNote(cmpStay)} />
            )}
            {cmpFood && (
              <CompareRow testId="compare-food" icon={UtensilsCrossed} color="text-[#E07A5F]" label="Eten & drinken" actual={cmpFood.actual} planned={cmpFood.planned}
                note={cmpNote(cmpFood)} />
            )}
            {(tollActual > 0 || tollPlanned > 0) && (
              <CompareRow testId="compare-tolls" icon={Coins} color="text-[#B5838D]" label="Tol / péage" actual={tollActual} planned={tollPlanned}
                note={tollPlanned > 0 ? "werkelijk = onkosten met categorie tol of tol/péage/vignet in de naam" : "geen raming ingesteld — gebruik het potlood bij Tol / péage hierboven"} />
            )}
            {otherExtras > 0.005 && (
              <CompareRow testId="compare-other" icon={Receipt} color="text-muted-foreground" label="Overige onkosten" actual={otherExtras} planned={null}
                note="buiten de raming (parkeren, tickets, ferry...)" />
            )}
          </div>
        </div>
      )}

      <DupeDetector tripId={trip.id} extras={extras} expenses={splitData.expenses} onRefresh={onRefresh} />

      <ExtraCostsCard tripId={trip.id} perDay={budget.per_day} extras={extras}
        members={splitData.members} expenses={splitData.expenses} onRefresh={onRefresh}
        tricountUrl={trip.tricount_url} tricountNameMap={trip.tricount_name_map} tricountAutoSync={trip.tricount_auto_sync} tricountPushed={trip.tricount_pushed} />

      {chartReady && <CategoryPie extras={extras} travelMode={trip.travel_mode} />}

      {chartReady ? (
        <BudgetChart perDay={budget.per_day || []} extras={extras} dailyBudget={trip.daily_budget} />
      ) : (
        (budget.per_day || []).length >= 2 && <div className="bg-card rounded-2xl p-5 h-[286px]" data-testid="budget-chart-placeholder" />
      )}

      <PerDayList tripId={trip.id} perDay={budget.per_day} extras={extras} dailyBudget={trip.daily_budget}
        travelMode={trip.travel_mode} members={splitData.members} expenses={splitData.expenses} onRefresh={onRefresh} />

      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground flex-1">{budget.assumptions} Voeg je werkelijke uitgaven toe via "Onkosten & uitgaven" — met een dag erbij verschijnen ze bij die dag.</p>
        <button data-testid="budget-recalc-button" onClick={recalc} disabled={loading}
          className="text-xs font-semibold text-primary hover:text-[#1B4332] flex items-center gap-1 shrink-0 transition-[color] duration-200">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Herbereken
        </button>
      </div>
    </div>
  );
};
